import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { Blocks } from "react-loader-spinner";
import TextareaAutosize from '@mui/material/TextareaAutosize';
import Button from '@mui/material/Button';
import Backdrop from "@mui/material/Backdrop";
import { updateGeneralInfo } from '../../../store/modules/generalData/actions';
import FinancialHandler from "../../../handlers/FinancialHandler";
import CountrySelect from './CountrySelect';
import * as styles from './styles';

function GeneralInfo({ generalInfo, dispatchUpdateGeneralInfo }) {
  const [ logo, setLogo ] = useState(generalInfo?.logo);
  const [ isLinkValid, setIsLinkValid ] = useState(generalInfo?.website?.includes("http"));
  const [ loading, setLoading ] = useState(false);

  // onDrop reads file as data-URL and dispatches to Redux
  const onDrop = acceptedFiles => {
    const file = acceptedFiles[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      dispatchUpdateGeneralInfo("logo", reader.result);
      setLogo(reader.result);
    };
    reader.readAsDataURL(file);
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/*'
  });

  const handleChange = (name, value) => {
    dispatchUpdateGeneralInfo(name, value);
    if(name === "website") setIsLinkValid(value.includes("http"));
  }

  const handleWebSearch = async () => {
    setLoading(true);
    
    const [data, overview, economics, industry] = await Promise.all([
      FinancialHandler.fetchGPTSearchCompany(generalInfo?.website),
      FinancialHandler.fetchGPTSearchReport({ type: "Business", link: generalInfo?.website }),
      FinancialHandler.fetchGPTSearchReport({ type: "Economic", link: generalInfo?.website }),
      FinancialHandler.fetchGPTSearchReport({ type: "Industry", link: generalInfo?.website }),
    ]);

    Object.keys(data).forEach(key => handleChange(key, data[key]));
    handleChange("overview", overview);
    handleChange("economics", economics);
    handleChange("industry", industry);

    setLoading(false);
  }

  return ( loading ? (
      <>
        <div style={{ display: "flex", justifyContent: "center", margin: "30px" }} >
          <Backdrop sx={{ color: "fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading} >
            <Blocks visible={true} height="80" width="80" />
          </Backdrop>
        </div>
      </>
    ) : (
      <div style={styles.container}>
        <div style={styles.imageContainer}>
          <div {...getRootProps()} style={styles.dropzone}>
          <input {...getInputProps()} />
            {logo ? (
              <img src={logo} alt="Company Logo" style={styles.dropzoneImg} />
            ) : (
              <div style={{ textAlign: 'center', fontSize: 14, color: '#666' }}>
                {isDragActive ? 'Drop image here' : 'Drag & drop\nor click to upload'}
              </div>
            )}
          </div>
        </div>
        <div style={styles.underContainer}>
          <div style={styles.side}>
            <div className='flex justify-between mb-3'>
              <div className='flex items-center' style={{ width: "75%" }}>
                <label style={styles.label}>Website Link:</label>
                <TextareaAutosize
                  defaultValue={generalInfo?.website}
                  minRows={1}
                  maxRows={3}
                  placeholder='Website Link'
                  style={styles.textarea}
                  onChange={(e) => handleChange("website", e.target.value)}
                />
              </div>
              <Button
                variant="contained"
                size="small"
                color="secondary"
                disabled={!isLinkValid || loading}
                onClick={handleWebSearch}
              >
                {loading ? "Loading" : "Get Data from Link"}
              </Button>
            </div>
            <div style={styles.row}>
              <label style={styles.label}>Name of Business:</label>
              <TextareaAutosize
                defaultValue={generalInfo?.name}
                minRows={1}
                maxRows={3}
                placeholder='Name of Business'
                style={styles.textarea}
                onBlur={(e) => handleChange("name", e.target.value)}
              />
            </div>
            <div style={styles.row}>
              <label style={styles.label}>Business Address:</label>
              <TextareaAutosize
                defaultValue={generalInfo?.address}
                minRows={1}
                maxRows={3}
                placeholder='Business Address'
                style={styles.textarea}
                onBlur={(e) => handleChange("address", e.target.value)}
              />
            </div>
            <div className='flex justify-between mb-3'>
              <div className='flex items-center'>
                <label style={styles.label}>City:</label>
                <TextareaAutosize
                  defaultValue={generalInfo?.city}
                  minRows={1}
                  maxRows={3}
                  placeholder="City"
                  style={styles.textarea}
                  onBlur={(e) => handleChange("city", e.target.value)}
                />
              </div>
              <div className='flex items-center'>
                <label style={styles.shortLabel}>State:</label>
                <TextareaAutosize
                  defaultValue={generalInfo?.state}
                  minRows={1}
                  maxRows={3}
                  placeholder="State"
                  style={styles.textarea}
                  onBlur={(e) => handleChange("state", e.target.value)}
                />
              </div>
              <div className='flex items-center'>
                <label style={styles.shortLabel}>ZIP Code:</label>
                <TextareaAutosize
                  defaultValue={generalInfo?.zipCode}
                  minRows={1}
                  maxRows={3}
                  placeholder="ZIP Code"
                  style={styles.textarea}
                  onBlur={(e) => handleChange("zipCode", e.target.value)}
                />
              </div>
            </div>
            <div className='flex justify-between mb-3'>
              <div className='flex items-center'>
                <label style={styles.label}>Country:</label>
                <CountrySelect
                  value={generalInfo?.country}
                  onBlur={(val) => handleChange("country", val)}
                />
              </div>
              <div className='flex items-center'>
                <label style={styles.shortLabel}>DUNS Number:</label>
                <TextareaAutosize
                  defaultValue={generalInfo?.dunsNumber}
                  minRows={1}
                  maxRows={3}
                  placeholder="DUNS Number"
                  style={styles.textarea}
                  onBlur={(e) => handleChange("dunsNumber", e.target.value)}
                />
              </div>
            </div>
            <div style={styles.row}>
              <label style={styles.label}>Business Overview:</label>
              <TextareaAutosize
                defaultValue={generalInfo?.overview}
                minRows={6}
                maxRows={6}
                placeholder='Business Overview'
                style={styles.textarea}
                onBlur={(e) => handleChange("overview", e.target.value)}
              />
            </div>
          </div>

          <div style={styles.side}>
            <div style={styles.row}>
              <label style={styles.label}>Economics Report:</label>
              <TextareaAutosize
                defaultValue={generalInfo?.economics}
                minRows={6}
                maxRows={6}
                placeholder='Economics Report'
                style={styles.textarea}
                onBlur={(e) => handleChange("economics", e.target.value)}
              />
            </div>
            <div style={styles.row}>
              <label style={styles.label}>Industry Report:</label>
              <TextareaAutosize
                defaultValue={generalInfo?.industry}
                minRows={6}
                maxRows={6}
                placeholder='Industry Report'
                style={styles.textarea}
                onBlur={(e) => handleChange("industry", e.target.value)}
              />
            </div>
            <div className='flex justify-between mb-3'>
              <div className='flex items-center'>
                <label style={styles.label}>Primary NAICS:</label>
                <TextareaAutosize
                  defaultValue={generalInfo?.pNAICS}
                  minRows={1}
                  maxRows={3}
                  placeholder="Primary NAICS"
                  style={styles.textarea}
                  onBlur={(e) => handleChange("pNAICS", e.target.value)}
                />
              </div>
              <div className='flex items-center'>
                <label style={styles.shortLabel}>SIC:</label>
                <TextareaAutosize
                  defaultValue={generalInfo?.pSIC}
                  minRows={1}
                  maxRows={3}
                  placeholder="Primary SIC"
                  style={styles.textarea}
                  onBlur={(e) => handleChange("pSIC", e.target.value)}
                />
              </div>
              <div className='flex items-center'>
                <label style={styles.shortLabel}>ISIC:</label>
                <TextareaAutosize
                  defaultValue={generalInfo?.pISIC}
                  minRows={1}
                  maxRows={3}
                  placeholder="Primary ISIC"
                  style={styles.textarea}
                  onBlur={(e) => handleChange("pISIC", e.target.value)}
                />
              </div>
            </div>
            <div className='flex justify-between mb-3'>
              <div className='flex items-center'>
                <label style={styles.label}>Secondary NAICS:</label>
                <TextareaAutosize
                  defaultValue={generalInfo?.sNAICS}
                  minRows={1}
                  maxRows={3}
                  placeholder="Secondary NAICS"
                  style={styles.textarea}
                  onBlur={(e) => handleChange("sNAICS", e.target.value)}
                />
              </div>
              <div className='flex items-center'>
                <label style={styles.shortLabel}>SIC:</label>
                <TextareaAutosize
                  defaultValue={generalInfo?.sSIC}
                  minRows={1}
                  maxRows={3}
                  placeholder="Secondary SIC"
                  style={styles.textarea}
                  onBlur={(e) => handleChange("sSIC", e.target.value)}
                />
              </div>
              <div className='flex items-center'>
                <label style={styles.shortLabel}>ISIC:</label>
                <TextareaAutosize
                  defaultValue={generalInfo?.sISIC}
                  minRows={1}
                  maxRows={3}
                  placeholder="Secondary ISIC"
                  style={styles.textarea}
                  onBlur={(e) => handleChange("sISIC", e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );
}

const mapStateToProps = (state) => ({
  generalInfo: state.generalData.generalInfo
});

const mapDispatchToProps = (dispatch) => ({
  dispatchUpdateGeneralInfo(name, value) {
    dispatch(updateGeneralInfo(name, value));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(GeneralInfo);